import { supabase } from '@/core/database/supabase';
import type { 
  DesignDocument, 
  DesignDocumentStats, 
  DesignDocumentFilters 
} from '../types/design-documents.types';

export const DesignDocumentsService = {
  /**
   * Danh sách hồ sơ thiết kế
   */
  async getDocuments(companyId: string, filters: DesignDocumentFilters = {}): Promise<{ data: DesignDocument[], total: number }> {
    let query = supabase
      .from('project_design_documents')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        phase:project_phases(id, name),
        uploader:user_profiles!uploaded_by(id, full_name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.type && filters.type !== 'all') query = query.eq('document_type', filters.type);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.zoneId) query = query.eq('zone_id', filters.zoneId);
    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,document_code.ilike.%${filters.search}%`);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as DesignDocument[],
      total: count || 0
    };
  },

  /**
   * Chi tiết hồ sơ
   */
  async getDocumentDetail(id: string): Promise<DesignDocument> {
    const { data, error } = await supabase
      .from('project_design_documents')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        phase:project_phases(id, name),
        uploader:user_profiles!uploaded_by(id, full_name)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as DesignDocument;
  },

  /**
   * Thống kê hồ sơ thiết kế
   */
  async getDocumentStats(companyId: string, projectId?: string): Promise<DesignDocumentStats> {
    let query = supabase.from('project_design_documents').select('status_code, revision_no').eq('company_id', companyId);
    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;
    if (error) throw error;

    return {
      totalDocuments: data.length,
      forConstructionCount: data.filter(d => d.status_code === 'for_construction').length,
      underReviewCount: data.filter(d => d.status_code === 'under_review').length,
      latestRevisionsCount: data.filter(d => d.revision_no > 0).length,
    };
  },

  /**
   * CRUD
   */
  async uploadDocument(data: Partial<DesignDocument>): Promise<DesignDocument> {
    const { data: result, error } = await supabase
      .from('project_design_documents')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as DesignDocument;
  },

  async updateDocument(id: string, updates: Partial<DesignDocument>): Promise<void> {
    const { error } = await supabase
      .from('project_design_documents')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  }
};
